import {
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AdminCompany,
  type AdminDocType,
  useAdminMutation,
  useAdminQuery,
} from "../../api/admin";
import { api } from "../../api/client";
import { useModule } from "../../api/hooks";

type CompanyForm = Omit<AdminCompany, "id" | "documents">;
type DocTypeForm = Omit<AdminDocType, "id" | "documents">;

const EMPTY_COMPANY: CompanyForm = {
  code: "",
  nameHy: "",
  nameRu: "",
  nameEn: "",
  taxId: "",
  folderName: "",
  mulberryBaseUrl: "",
  cloudLlmAllowed: false,
  active: true,
  aliases: [],
};
const EMPTY_TYPE: DocTypeForm = {
  code: "",
  nameHy: "",
  nameRu: "",
  nameEn: "",
  folderName: "",
  sortOrder: 100,
  keywords: [],
};

const nullable = (s: string | null) => (s?.trim() ? s.trim() : null);

export function CompaniesPage() {
  const { t } = useTranslation();
  const list = useAdminQuery<{ items: AdminCompany[] }>("companies", "/api/admin/companies");
  const [editing, setEditing] = useState<AdminCompany | "new" | null>(null);
  const [form, setForm] = useState<CompanyForm>(EMPTY_COMPANY);
  const save = useAdminMutation(
    async (v: { id: number | null; form: CompanyForm }) => {
      const body = {
        code: nullable(v.form.code),
        nameHy: v.form.nameHy.trim(),
        nameRu: nullable(v.form.nameRu),
        nameEn: nullable(v.form.nameEn),
        taxId: nullable(v.form.taxId),
        folderName: nullable(v.form.folderName),
        mulberryBaseUrl: nullable(v.form.mulberryBaseUrl),
        cloudLlmAllowed: v.form.cloudLlmAllowed,
        active: v.form.active,
        aliases: v.form.aliases,
      };
      return v.id === null
        ? api("/api/admin/companies", { method: "POST", body })
        : api(`/api/admin/companies/${v.id}`, { method: "PATCH", body });
    },
    ["companies"],
  );
  const [aliasesText, setAliasesText] = useState("");
  const [removing, setRemoving] = useState<AdminCompany | null>(null);
  const remove = useAdminMutation(
    (id: number) => api(`/api/admin/companies/${id}`, { method: "DELETE" }),
    ["companies"],
  );
  const open = (c: AdminCompany | "new") => {
    setEditing(c);
    setForm(c === "new" ? EMPTY_COMPANY : { ...c });
    setAliasesText(c === "new" ? "" : (c.aliases ?? []).join(", "));
  };
  const detectOn = useModule("detect");
  const detect = useAdminMutation(
    () =>
      api<{ scanned: number; assigned: number; ambiguous: number }>("/api/admin/companies/detect", {
        method: "POST",
      }),
    ["companies"],
  );
  const field = (key: keyof CompanyForm, label: string, description?: string) => (
    <TextInput
      label={label}
      value={(form[key] as string | null) ?? ""}
      onChange={(e) => setForm({ ...form, [key]: e.currentTarget.value })}
      {...(description ? { description } : {})}
    />
  );

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {t("admin.companies.hint")}
        </Text>
        <Group gap="xs">
          <Button
            variant="default"
            hidden={!detectOn}
            loading={detect.isPending}
            title={t("admin.companies.detectHint")}
            onClick={() =>
              detect
                .mutateAsync(undefined)
                .then((r) =>
                  notifications.show({
                    message: t("admin.companies.detected", r),
                    color: r.assigned ? "green" : "gray",
                  }),
                )
                .catch((e: Error) => notifications.show({ message: e.message, color: "red" }))
            }
          >
            {t("admin.companies.detect")}
          </Button>
          <Button onClick={() => open("new")}>{t("admin.companies.add")}</Button>
        </Group>
      </Group>
      <Table.ScrollContainer minWidth={640}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("admin.ref.code")}</Table.Th>
              <Table.Th>{t("admin.ref.nameHy")}</Table.Th>
              <Table.Th>{t("admin.ref.folder")}</Table.Th>
              <Table.Th ta="right">{t("admin.ref.documents")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(list.data?.items ?? []).map((c) => (
              <Table.Tr key={c.id} style={{ opacity: c.active ? 1 : 0.5 }}>
                <Table.Td>{c.code}</Table.Td>
                <Table.Td>
                  {c.nameHy}
                  {!c.active && (
                    <Badge ml={6} size="xs" color="gray">
                      {t("admin.ref.inactive")}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>{c.folderName ?? ""}</Table.Td>
                <Table.Td ta="right">{c.documents}</Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Button size="compact-xs" variant="subtle" onClick={() => open(c)}>
                      {t("common.edit")}
                    </Button>
                    {c.code !== "unassigned" && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        disabled={c.documents > 0}
                        title={c.documents > 0 ? t("admin.companies.deleteBlocked") : undefined}
                        onClick={() => setRemoving(c)}
                      >
                        {t("common.delete")}
                      </Button>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        title={t("common.delete")}
      >
        <Stack>
          <Text size="sm">
            {t("admin.companies.deleteConfirm", { name: removing?.nameHy ?? "" })}
          </Text>
          <Text size="xs" c="dimmed">
            {t("admin.companies.deleteHint")}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRemoving(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={remove.isPending}
              onClick={() =>
                removing &&
                remove
                  .mutateAsync(removing.id)
                  .then(() => setRemoving(null))
                  .catch((e: Error) => notifications.show({ message: e.message, color: "red" }))
              }
            >
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={t("admin.companies.edit")}
      >
        <Stack gap="xs">
          {field("nameHy", t("admin.ref.nameHy"))}
          {field("nameRu", t("admin.ref.nameRu"))}
          {field("nameEn", t("admin.ref.nameEn"))}
          {field("code", t("admin.ref.codeOptional"), t("admin.ref.codeHint"))}
          {field("taxId", t("admin.companies.taxId"))}
          <TextInput
            label={t("admin.companies.aliases")}
            description={t("admin.companies.aliasesHint")}
            value={aliasesText}
            onChange={(e) => {
              setAliasesText(e.currentTarget.value);
              setForm({
                ...form,
                aliases: e.currentTarget.value
                  .split(/[,;\n]/)
                  .map((s) => s.trim())
                  .filter(Boolean),
              });
            }}
          />
          {field("folderName", t("admin.ref.folderOptional"), t("admin.ref.folderHint"))}
          {field(
            "mulberryBaseUrl",
            t("admin.companies.mulberry"),
            t("admin.companies.mulberryHint"),
          )}
          <Switch
            label={t("admin.companies.cloudLlm")}
            checked={form.cloudLlmAllowed}
            onChange={(e) => setForm({ ...form, cloudLlmAllowed: e.currentTarget.checked })}
          />
          <Switch
            label={t("admin.ref.active")}
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.currentTarget.checked })}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={save.isPending}
              disabled={!form.nameHy.trim()}
              onClick={async () => {
                try {
                  await save.mutateAsync({
                    id: editing === "new" ? null : (editing as AdminCompany).id,
                    form,
                  });
                  notifications.show({ message: t("common.saved"), color: "green" });
                  setEditing(null);
                } catch (e) {
                  notifications.show({ message: (e as Error).message, color: "red" });
                }
              }}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export function DocTypesPage() {
  const { t } = useTranslation();
  const list = useAdminQuery<{ items: AdminDocType[] }>("doc-types", "/api/admin/doc-types");
  const [editing, setEditing] = useState<AdminDocType | "new" | null>(null);
  const [form, setForm] = useState<DocTypeForm>(EMPTY_TYPE);
  const save = useAdminMutation(
    async (v: { id: number | null; form: DocTypeForm }) => {
      const body = {
        code: nullable(v.form.code),
        nameHy: v.form.nameHy.trim(),
        nameRu: nullable(v.form.nameRu),
        nameEn: nullable(v.form.nameEn),
        folderName: nullable(v.form.folderName),
        sortOrder: v.form.sortOrder,
        keywords: v.form.keywords,
      };
      return v.id === null
        ? api("/api/admin/doc-types", { method: "POST", body })
        : api(`/api/admin/doc-types/${v.id}`, { method: "PATCH", body });
    },
    ["doc-types"],
  );
  const [keywordsText, setKeywordsText] = useState("");
  const open = (d: AdminDocType | "new") => {
    setEditing(d);
    setForm(d === "new" ? EMPTY_TYPE : { ...d });
    setKeywordsText(d === "new" ? "" : (d.keywords ?? []).join(", "));
  };
  const detectOn = useModule("detect");
  const detect = useAdminMutation(
    () =>
      api<{ scanned: number; assigned: number; ambiguous: number }>("/api/admin/doc-types/detect", {
        method: "POST",
      }),
    ["doc-types"],
  );
  const field = (key: keyof DocTypeForm, label: string, description?: string) => (
    <TextInput
      label={label}
      value={(form[key] as string | null) ?? ""}
      onChange={(e) => setForm({ ...form, [key]: e.currentTarget.value })}
      {...(description ? { description } : {})}
    />
  );

  return (
    <Stack gap="sm">
      <Group justify="flex-end" gap="xs">
        <Button
          variant="default"
          hidden={!detectOn}
          loading={detect.isPending}
          title={t("admin.docTypes.detectHint")}
          onClick={() =>
            detect
              .mutateAsync(undefined)
              .then((r) =>
                notifications.show({
                  message: t("admin.companies.detected", r),
                  color: r.assigned ? "green" : "gray",
                }),
              )
              .catch((e: Error) => notifications.show({ message: e.message, color: "red" }))
          }
        >
          {t("admin.docTypes.detect")}
        </Button>
        <Button onClick={() => open("new")}>{t("admin.docTypes.add")}</Button>
      </Group>
      <Table.ScrollContainer minWidth={640}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("admin.ref.code")}</Table.Th>
              <Table.Th>{t("admin.ref.nameHy")}</Table.Th>
              <Table.Th>{t("admin.ref.folder")}</Table.Th>
              <Table.Th ta="right">{t("admin.docTypes.order")}</Table.Th>
              <Table.Th ta="right">{t("admin.ref.documents")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(list.data?.items ?? []).map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>{d.code}</Table.Td>
                <Table.Td>{d.nameHy}</Table.Td>
                <Table.Td>{d.folderName ?? ""}</Table.Td>
                <Table.Td ta="right">{d.sortOrder}</Table.Td>
                <Table.Td ta="right">{d.documents}</Table.Td>
                <Table.Td>
                  <Button size="compact-xs" variant="subtle" onClick={() => open(d)}>
                    {t("common.edit")}
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={t("admin.docTypes.edit")}
      >
        <Stack gap="xs">
          {field("code", t("admin.ref.codeOptional"), t("admin.ref.codeHint"))}
          {field("nameHy", t("admin.ref.nameHy"))}
          {field("nameRu", t("admin.ref.nameRu"))}
          {field("nameEn", t("admin.ref.nameEn"))}
          {field("folderName", t("admin.ref.folderOptional"), t("admin.ref.folderHint"))}
          <TextInput
            label={t("admin.docTypes.keywords")}
            description={t("admin.docTypes.keywordsHint")}
            value={keywordsText}
            onChange={(e) => {
              setKeywordsText(e.currentTarget.value);
              setForm({
                ...form,
                keywords: e.currentTarget.value
                  .split(/[,;\n]/)
                  .map((x) => x.trim())
                  .filter(Boolean),
              });
            }}
          />
          <NumberInput
            label={t("admin.docTypes.order")}
            value={form.sortOrder}
            onChange={(v) => setForm({ ...form, sortOrder: typeof v === "number" ? v : 100 })}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={save.isPending}
              disabled={!form.nameHy.trim()}
              onClick={async () => {
                try {
                  await save.mutateAsync({
                    id: editing === "new" ? null : (editing as AdminDocType).id,
                    form,
                  });
                  notifications.show({ message: t("common.saved"), color: "green" });
                  setEditing(null);
                } catch (e) {
                  notifications.show({ message: (e as Error).message, color: "red" });
                }
              }}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
